import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { cn } from '@hqo/react-hooks';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@hqo/ui-atoms';
import { COMMENT_MAX_LENGTH } from 'app/data/types/feedback';
import { useMobileScreen } from 'app/shared/hooks/use-mobile-screen';

interface RatingCommentModalProps {
  isOpen: boolean;
  /** The rating already chosen by tapping a star — this modal only collects the words. */
  rating: number;
  /** Send the rating together with the comment. */
  onSubmit: (comment: string) => void;
  /** Send the rating alone — the comment is optional, so a dismissal must not lose the tap. */
  onSkip: () => void;
}

/**
 * Optional comment step, shown after a star tap where the building collects comments
 * (`comments_enabled`).
 *
 * The rating is NOT yet submitted when this opens: feedback-service takes rating and comment in one
 * atomic call and rejects a second submit for the same candidate, so there is no "rate now, add words
 * later". Both exits here therefore submit exactly once — with the comment, or without it. Dismissing
 * counts as without, because tapping a star was already the answer.
 */
export const RatingCommentModal = ({ isOpen, rating, onSubmit, onSkip }: RatingCommentModalProps): JSX.Element => {
  const intl = useIntl();
  const { isMobileScreen } = useMobileScreen();
  const [comment, setComment] = useState('');

  // Each open starts empty. This dialog stays mounted (house pattern, so it can animate out), so
  // without this the text typed for one activity is still sitting there when another is rated — and
  // submitting would file those words against the wrong candidate.
  useEffect(() => {
    if (isOpen) {
      setComment('');
    }
  }, [isOpen]);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => setComment(event.target.value), []);

  const handleSubmit = useCallback(() => {
    const trimmed = comment.trim();

    // An empty box is the same intent as skipping — don't post a blank comment.
    if (trimmed) {
      onSubmit(trimmed);
    } else {
      onSkip();
    }
  }, [comment, onSubmit, onSkip]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onSkip();
    },
    [onSkip],
  );

  /**
   * Radix focuses the first focusable child on open, which is the textarea below.
   *
   * On a phone that raises the native keyboard mid-animation: the sheet is still sliding up while the
   * keyboard takes the bottom of the screen, so the field the user is meant to type into ends up
   * behind it. They can tap it themselves once the sheet has settled.
   */
  const preventAutoFocus = useCallback((event: Event) => event.preventDefault(), []);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'border-none p-6',
          isMobileScreen
            ? [
              'bottom-0 left-0 top-auto w-full max-w-full translate-x-0 translate-y-0 rounded-b-none rounded-t-2xl sm:max-w-full',
              'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom duration-500',
            ]
            : 'rounded-2xl sm:max-w-[440px]',
        )}
        onOpenAutoFocus={preventAutoFocus}
        data-testid="rating-comment-modal"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-semibold text-foreground">
            {intl.formatMessage({ id: 'activity.rating.comment.title' })}
          </DialogTitle>
        </DialogHeader>
        <textarea
          value={comment}
          onChange={handleChange}
          // Capped to what the service accepts, so a long comment can't come back as a 400.
          maxLength={COMMENT_MAX_LENGTH}
          rows={4}
          aria-label={intl.formatMessage({ id: 'activity.rating.comment.placeholder' })}
          placeholder={intl.formatMessage({ id: 'activity.rating.comment.placeholder' })}
          data-testid="rating-comment-input"
          className="w-full resize-none rounded-xl border border-system-grey-medium bg-background p-3 text-[15px] leading-6 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <DialogFooter className="mt-1 flex flex-col gap-2.5 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={rating < 1}
            className="h-12 w-full text-sm font-semibold"
            data-testid="rating-comment-submit"
          >
            {intl.formatMessage({ id: 'activity.rating.comment.submit' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
            className="h-12 w-full text-sm font-semibold hover:bg-system-grey-light hover:text-foreground"
            data-testid="rating-comment-skip"
          >
            {intl.formatMessage({ id: 'activity.rating.comment.skip' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
